import { RefAreaValue, ZoomBoundary } from '../../types';

type ValidateLeftAreaArgs<T> = {
  dataMapByXAxisKey: Map<T[keyof T], number>;
  value: RefAreaValue;
  minValue?: ZoomBoundary;
  maxValue?: ZoomBoundary;
};

export const validateLeftArea = <T>({
  dataMapByXAxisKey,
  value,
  minValue,
  maxValue,
}: ValidateLeftAreaArgs<T>): RefAreaValue => {
  const safeValue =
    typeof value === 'number' || typeof value === 'string' || value === null
      ? value
      : null;

  if (safeValue === null) {
    return safeValue;
  }

  const getItemIndex = (key: ZoomBoundary): number =>
    dataMapByXAxisKey.get(key as T[keyof T]) ?? -1;

  const valueIndex = getItemIndex(safeValue);

  // can't start zooming from section that is before min value
  if (minValue !== undefined) {
    const minValueIndex = getItemIndex(minValue);
    if (valueIndex < minValueIndex) {
      return null;
    }
  }

  // can't start zooming from section that is after max value
  if (maxValue !== undefined) {
    const maxValueIndex = getItemIndex(maxValue);
    if (valueIndex > maxValueIndex) {
      return null;
    }
  }

  return safeValue;
};

type ValidateRightAreaArgs<T> = {
  dataMapByXAxisKey: Map<T[keyof T], number>;
  value: RefAreaValue;
  minValue?: ZoomBoundary;
  maxValue?: ZoomBoundary;
  leftAreaValue: RefAreaValue;
};

export const validateRightArea = <T>({
  dataMapByXAxisKey,
  value,
  minValue,
  maxValue,
  leftAreaValue,
}: ValidateRightAreaArgs<T>): RefAreaValue => {
  const safeValue =
    typeof value === 'number' || typeof value === 'string' || value === null
      ? value
      : null;

  if (safeValue === null || leftAreaValue === null) {
    return safeValue;
  }

  const getItemIndex = (key: ZoomBoundary): number =>
    dataMapByXAxisKey.get(key as T[keyof T]) ?? -1;

  const valueIndex = getItemIndex(safeValue);
  const leftAreaIndex = getItemIndex(leftAreaValue);

  if (minValue !== undefined && valueIndex < leftAreaIndex) {
    const minValueIndex = getItemIndex(minValue);
    if (valueIndex < minValueIndex) {
      return minValue;
    }
  }

  if (maxValue !== undefined && valueIndex > leftAreaIndex) {
    const maxValueIndex = getItemIndex(maxValue);
    if (valueIndex > maxValueIndex) {
      return maxValue;
    }
  }

  return safeValue;
};
